import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Mail, Users, MailCheck, MailX, MousePointerClick, Eye, MailMinus,
  Clock, RefreshCw, AlertCircle, CheckCircle2, Send, Loader2, X,
  Link2, ShieldAlert, Ban, CalendarDays, Percent,
} from "lucide-react";
import { toast } from "sonner";
import RecipientDrillDown from "./RecipientDrillDown";

/**
 * Admin → Email Campaigns tab.
 *
 * Pure stats dashboard — admin picks one of OUR internal campaigns, links it
 * once to a Mailjet marketing campaign via the "Link Mailjet Campaign"
 * dropdown, and afterwards sees live stats merged from:
 *   - Mailjet `/statcounters` (60s cache) — authoritative aggregate
 *   - Local `email_events` populated by Mailjet event webhooks — instant
 *
 * Per-metric we take `max(mailjet_poll, local_events)` so the tile reflects
 * whichever signal is currently ahead. No bulk-send UI lives here — sends
 * happen inside Mailjet's UI; we just surface the results.
 */
export default function AdminEmailCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sendSubject, setSendSubject] = useState("");
  const [sending, setSending] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [diag, setDiag] = useState(null);
  // Bumped every time the stats endpoint is hit — drives a fresh fetch in
  // the per-recipient drill-down so the table stays in sync after a manual
  // Refresh click or a re-link.
  const [refreshKey, setRefreshKey] = useState(0);

  // Mailjet marketing-campaign linking
  const [mjList, setMjList] = useState([]);
  const [mjListLoading, setMjListLoading] = useState(false);
  const [mjListError, setMjListError] = useState(null);
  const [linkSaving, setLinkSaving] = useState(false);
  // Staged (not-yet-saved) Mailjet campaign id. Saved only on Save click so
  // admins can review their selection without firing a PUT each tap.
  const [pendingMjId, setPendingMjId] = useState("");

  const loadDiag = async () => {
    try {
      const { data } = await api.get("/mailjet/webhooks/diag");
      setDiag(data);
    } catch {
      setDiag(null);
    }
  };

  useEffect(() => {
    if (health?.ok) loadDiag();
    // Refresh diag every 15s so newly-arrived webhooks show without manual reload
    const id = setInterval(() => { if (health?.ok) loadDiag(); }, 15000);
    return () => clearInterval(id);
  }, [health?.ok]);

  const loadMjList = async () => {
    setMjListLoading(true);
    setMjListError(null);
    try {
      const { data } = await api.get("/email-campaigns/mailjet/list?limit=200");
      setMjList(data.campaigns || []);
    } catch (e) {
      setMjListError(e.response?.data?.detail || "Failed to load Mailjet campaigns");
    } finally { setMjListLoading(false); }
  };

  useEffect(() => {
    if (health?.ok) loadMjList();
  }, [health?.ok]);

  const registerWebhooks = async () => {
    if (!window.confirm("Register THIS server as Mailjet's event-webhook endpoint for opens/clicks/bounces/unsubs? Existing registrations for those event types will be overwritten.")) return;
    setRegistering(true);
    try {
      const { data } = await api.post("/email-campaigns/register-webhooks");
      const ok = data.events.filter((e) => e.ok).length;
      toast.success(`Webhooks registered for ${ok}/${data.events.length} event types`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Webhook registration failed");
    } finally { setRegistering(false); }
  };

  // Load campaigns list + Mailjet health on mount
  useEffect(() => {
    (async () => {
      try {
        const [cRes, hRes] = await Promise.all([
          api.get("/campaigns"),
          api.get("/email-campaigns/health"),
        ]);
        setCampaigns(cRes.data || []);
        setHealth(hRes.data);
      } catch {
        // health endpoint failure is non-fatal — banner shows the warning
      } finally { setLoading(false); }
    })();
  }, []);

  // Fetch stats whenever the selection changes
  useEffect(() => {
    if (!selectedId) { setStats(null); setPendingMjId(""); return; }
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      try {
        const { data } = await api.get(`/email-campaigns/${selectedId}/stats`);
        if (!cancelled) {
          setStats(data);
          // Sync staged value to whatever is currently saved.
          setPendingMjId(data?.campaign?.mailjet_campaign_id ? String(data.campaign.mailjet_campaign_id) : "");
        }
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load stats");
      } finally { if (!cancelled) setStatsLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [selectedId]);

  const refresh = async () => {
    if (!selectedId) return;
    setStatsLoading(true);
    try {
      const { data } = await api.get(`/email-campaigns/${selectedId}/stats`);
      setStats(data);
      setRefreshKey((k) => k + 1);
      toast.success("Stats refreshed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setStatsLoading(false); }
  };

  const sendTest = async () => {
    if (!sendTo) { toast.error("Recipient required"); return; }
    setSending(true);
    try {
      const body = { recipient_email: sendTo };
      if (sendSubject.trim()) body.subject = sendSubject.trim();
      const { data: r } = await api.post(`/email-campaigns/${selectedId}/send-test`, body);
      toast.success(`Sent · message_id ${r.message_id}`);
      setShowSend(false);
      setSendTo("");
      setSendSubject("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const linkMailjetCampaign = async (mjId) => {
    if (!selectedId) return;
    setLinkSaving(true);
    try {
      const body = { mailjet_campaign_id: mjId ? Number(mjId) : null };
      await api.put(`/email-campaigns/${selectedId}/link-mailjet`, body);
      toast.success(mjId ? "Linked to Mailjet campaign" : "Unlinked from Mailjet");
      // Force-refresh stats so the new metadata + counters show immediately
      const { data } = await api.get(`/email-campaigns/${selectedId}/stats`);
      setStats(data);
      setRefreshKey((k) => k + 1);
      // Reset pending state to match the now-saved value
      setPendingMjId(data?.campaign?.mailjet_campaign_id ? String(data.campaign.mailjet_campaign_id) : "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link failed");
    } finally { setLinkSaving(false); }
  };

  const mj = stats?.mailjet || {};
  const local = stats?.local_events || {};
  const linked = stats?.mailjet_linked;

  // Tiles take the MAX of (Mailjet poll, local-webhook events) per metric.
  const t = {
    sent: Math.max(mj.MessageSentCount || 0, local.sent || 0),
    delivered: Math.max(mj.MessageDeliveredCount || 0, local.delivered || 0),
    opens: Math.max(mj.MessageOpenedCount || 0, local.opens || 0),
    clicks: Math.max(mj.MessageClickedCount || 0, local.clicks || 0),
    bounces: Math.max(mj.MessageBouncedCount || 0, local.bounces || 0),
    unsubs: Math.max(mj.MessageUnsubscribedCount || 0, local.unsubs || 0),
    spams: Math.max(mj.MessageSpamCount || 0, local.spams || 0),
    blocked: mj.MessageBlockedCount || 0,
  };

  const openRate = t.delivered ? (t.opens / t.delivered) * 100 : 0;
  const clickRate = t.delivered ? (t.clicks / t.delivered) * 100 : 0;
  const anyData = Object.values(t).some((v) => v > 0);

  const sendDate = mj?.send_date ? new Date(mj.send_date).toLocaleString() : null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold">Email Campaigns</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Pick one of your internal campaigns, link it to a Mailjet marketing campaign once,
          and see open / click / bounce / unsubscribe / spam / block stats live — both poll-based
          (60s) and real-time via webhooks.
        </p>
      </div>

      {/* Mailjet health pill */}
      {health && (
        <div className="flex items-center gap-2 flex-wrap">
          <div
            data-testid="mailjet-health-banner"
            className={`border rounded-sm px-3 py-2 inline-flex items-center gap-2 text-xs ${
              health.ok
                ? "border-primary/40 bg-primary/5 text-primary"
                : "border-amber-500/40 bg-amber-50/40 text-amber-700 dark:bg-amber-950/20 dark:text-amber-400"
            }`}
          >
            {health.ok ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
            <span>
              Mailjet:{" "}
              <strong>{health.ok ? "Connected" : "Not configured"}</strong>
              {health.sender_email && health.sender_email !== "(sandbox)" && (
                <> · sending as <code className="font-mono">{health.sender_email}</code></>
              )}
              {(!health.sender_email || health.sender_email === "(sandbox)") && (
                <> · <em>sender domain not yet verified</em></>
              )}
            </span>
          </div>
          {health.ok && (
            <button
              onClick={registerWebhooks}
              disabled={registering}
              data-testid="register-webhooks-btn"
              className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50"
              title="Register this server as Mailjet's event-callback URL — opens / clicks / bounces flow into tiles in real time."
            >
              {registering ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              Register webhooks
            </button>
          )}
          {diag && (
            <div
              data-testid="webhook-diag"
              className="text-xs text-muted-foreground border border-border rounded-sm px-3 py-2"
              title={diag.last_event_at ? `Last event at unix ${diag.last_event_at}` : "No events yet"}
            >
              Webhook events received:{" "}
              <strong className="text-foreground">{diag.total_events}</strong>
              {diag.by_event_type && Object.keys(diag.by_event_type).length > 0 && (
                <> · {Object.entries(diag.by_event_type).map(([k, v]) => `${k}=${v}`).join(", ")}</>
              )}
            </div>
          )}
        </div>
      )}

      {/* Campaign selector + actions */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px] max-w-md">
          <label className="meta-label">Internal campaign</label>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={loading}
            data-testid="email-campaigns-select"
            className="form-input w-full mt-1"
          >
            <option value="">{loading ? "Loading…" : "— pick a campaign —"}</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {selectedId && (
          <div className="flex-1 min-w-[260px] max-w-md">
            <label className="meta-label inline-flex items-center gap-1">
              <Link2 size={11} /> Linked Mailjet campaign
            </label>
            <select
              value={pendingMjId}
              onChange={(e) => setPendingMjId(e.target.value)}
              disabled={linkSaving || mjListLoading || !health?.ok}
              data-testid="email-campaigns-link-mailjet"
              className="form-input w-full mt-1"
              title="Pick one of your Mailjet marketing campaigns, then click Save to persist the link."
            >
              <option value="">
                {mjListLoading ? "Loading Mailjet campaigns…" : "— not linked —"}
              </option>
              {mjList.map((c) => (
                <option key={c.id} value={c.id}>
                  {`[${statusLabel(c.status)}] ${c.title || c.subject || `Campaign #${c.id}`}`}
                  {c.send_date ? ` · ${new Date(c.send_date).toLocaleDateString()}` : ""}
                </option>
              ))}
            </select>
            <LinkActions
              pendingMjId={pendingMjId}
              savedMjId={stats?.campaign?.mailjet_campaign_id}
              linkSaving={linkSaving}
              onSave={() => linkMailjetCampaign(pendingMjId || null)}
              onReset={() => setPendingMjId(stats?.campaign?.mailjet_campaign_id ? String(stats.campaign.mailjet_campaign_id) : "")}
            />
            {mjListError && (
              <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                {mjListError}
              </div>
            )}
          </div>
        )}

        {selectedId && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSend(true)}
              disabled={!health?.ok}
              data-testid="email-campaigns-send-test"
              className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
              title={health?.ok ? "Send a real Mailjet email to verify the integration" : "Mailjet not connected"}
            >
              <Send size={11} /> Send test
            </button>
            <button
              onClick={refresh}
              disabled={statsLoading}
              data-testid="email-campaigns-refresh"
              className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50"
              title="Force-refresh from Mailjet (bypasses 60s cache)"
            >
              <RefreshCw size={11} className={statsLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        )}
      </div>

      {/* Linked-campaign meta row */}
      {selectedId && linked && (mj.subject || sendDate) && (
        <div
          data-testid="linked-campaign-meta"
          className="border border-border rounded-sm px-4 py-3 bg-secondary/20 text-xs flex flex-wrap gap-4"
        >
          {mj.subject && (
            <div>
              <div className="meta-label">Subject</div>
              <div className="font-medium mt-0.5">{mj.subject}</div>
            </div>
          )}
          {sendDate && (
            <div>
              <div className="meta-label inline-flex items-center gap-1">
                <CalendarDays size={10} /> Send date
              </div>
              <div className="font-medium mt-0.5">{sendDate}</div>
            </div>
          )}
          {mj.from_email && (
            <div>
              <div className="meta-label">From</div>
              <div className="font-medium mt-0.5"><code className="font-mono">{mj.from_email}</code></div>
            </div>
          )}
          {mj.status !== null && mj.status !== undefined && (
            <div>
              <div className="meta-label">Mailjet status</div>
              <div className="font-medium mt-0.5">{statusLabel(mj.status)}</div>
            </div>
          )}
        </div>
      )}

      {/* Stats tiles */}
      {!selectedId ? (
        <div className="border border-dashed border-border rounded-sm p-12 text-center bg-secondary/10">
          <Mail size={28} className="mx-auto text-muted-foreground" strokeWidth={1.5} />
          <div className="text-sm text-muted-foreground mt-2">Pick a campaign above to see its stats.</div>
        </div>
      ) : statsLoading && !stats ? (
        <div className="meta-label">Loading stats…</div>
      ) : stats ? (
        <>
          {!linked && !anyData && (
            <div
              data-testid="email-campaigns-not-linked"
              className="border border-amber-500/30 bg-amber-50/30 dark:bg-amber-950/20 rounded-sm px-4 py-3 text-xs flex items-start gap-2"
            >
              <Clock size={14} className="text-amber-600 mt-0.5 shrink-0" />
              <div>
                <strong>Not linked to a Mailjet campaign.</strong> Pick a Mailjet
                campaign from the dropdown above to pull its stats here, or
                send a test (the test fires with a <code className="font-mono">CustomID</code> tag so its
                opens / clicks land in this tile via webhooks regardless of linking).
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <Tile testid="tile-leads" icon={Users} label="Total leads available" value={stats.total_leads_available} hint="In our database for this campaign" />
            <Tile testid="tile-sent" icon={MailCheck} label="Total sent" value={t.sent} placeholder={t.sent === 0 && !linked} />
            <Tile testid="tile-delivered" icon={MailCheck} label="Delivered" value={t.delivered} placeholder={t.delivered === 0 && !linked} />
            <Tile testid="tile-opens" icon={Eye} label="Opens" value={t.opens} placeholder={t.opens === 0 && !linked && !anyData} />
            <Tile testid="tile-clicks" icon={MousePointerClick} label="Clicks" value={t.clicks} placeholder={t.clicks === 0 && !linked && !anyData} />
            <Tile testid="tile-open-rate" icon={Percent} label="Open rate" value={openRate} unit="%" decimals={2} placeholder={!t.delivered} />
            <Tile testid="tile-click-rate" icon={Percent} label="Click rate" value={clickRate} unit="%" decimals={2} placeholder={!t.delivered} />
            <Tile testid="tile-bounce" icon={MailX} label="Bounces" value={t.bounces} placeholder={t.bounces === 0 && !linked && !anyData} />
            <Tile testid="tile-unsub" icon={MailMinus} label="Unsubscribes" value={t.unsubs} placeholder={t.unsubs === 0 && !linked && !anyData} />
            <Tile testid="tile-spam" icon={ShieldAlert} label="Spam reports" value={t.spams} placeholder={t.spams === 0 && !linked && !anyData} />
            <Tile testid="tile-blocked" icon={Ban} label="Blocked" value={t.blocked} placeholder={t.blocked === 0 && !linked} />
            <Tile testid="tile-pending" icon={Clock} label="Pending promotion" value={Math.max(stats.total_leads_available - t.sent, 0)} hint="Leads not yet sent" />
          </div>

          {/* Per-recipient drill-down — joined to leads collection by email */}
          <RecipientDrillDown campaignId={selectedId} refreshSignal={refreshKey} />
        </>
      ) : null}

      {showSend && (
        <SendTestModal
          onClose={() => setShowSend(false)}
          sendTo={sendTo}
          setSendTo={setSendTo}
          sendSubject={sendSubject}
          setSendSubject={setSendSubject}
          stats={stats}
          health={health}
          sending={sending}
          onSend={sendTest}
        />
      )}
    </div>
  );
}

function statusLabel(s) {
  // Mailjet campaign status enum:
  // -2 = Deleted, -1 = Archived, 0 = Draft, 1 = Programmed, 2 = Sent
  const m = { "-2": "Deleted", "-1": "Archived", "0": "Draft", "1": "Programmed", "2": "Sent" };
  return m[String(s)] ?? `#${s}`;
}

function LinkActions({ pendingMjId, savedMjId, linkSaving, onSave, onReset }) {
  // Normalize both sides to strings so a numeric vs string id doesn't appear "dirty".
  const savedAsStr = savedMjId ? String(savedMjId) : "";
  const dirty = pendingMjId !== savedAsStr;
  const willClear = dirty && !pendingMjId;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <button
        onClick={onSave}
        disabled={!dirty || linkSaving}
        data-testid="email-campaigns-link-save"
        className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-40"
        title={dirty ? (willClear ? "Click to unlink" : "Click to save link") : "No pending changes"}
      >
        {linkSaving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
        {willClear ? "Unlink" : "Save link"}
      </button>
      {dirty && (
        <button
          onClick={onReset}
          disabled={linkSaving}
          data-testid="email-campaigns-link-reset"
          className="btn-secondary text-xs inline-flex items-center gap-1 disabled:opacity-50"
          title="Discard changes"
        >
          <X size={11} /> Reset
        </button>
      )}
      {!dirty && savedAsStr && (
        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
          <CheckCircle2 size={10} className="text-primary" />
          Linked — Mailjet ID <code className="font-mono">{savedAsStr}</code>
        </span>
      )}
      {dirty && (
        <span className="text-[11px] text-amber-700 dark:text-amber-400">
          Unsaved changes
        </span>
      )}
    </div>
  );
}

function Tile({ testid, icon: Icon, label, value, hint, placeholder = false, unit, decimals = 0 }) {
  let display = "—";
  if (!placeholder) {
    if (typeof value === "number") {
      display = decimals ? value.toFixed(decimals) : (value ?? 0).toLocaleString();
    } else {
      display = String(value ?? 0);
    }
    if (unit) display = `${display}${unit}`;
  }
  return (
    <div data-testid={testid} className="border border-border rounded-sm p-4 bg-card">
      <div className="flex items-center justify-between">
        <Icon size={14} className={placeholder ? "text-muted-foreground" : "text-primary"} />
      </div>
      <div className="meta-label mt-2">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${placeholder ? "text-muted-foreground" : ""}`}>{display}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function SendTestModal({ onClose, sendTo, setSendTo, sendSubject, setSendSubject, stats, health, sending, onSend }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="email-send-backdrop"
    >
      <div
        data-testid="email-send-modal"
        className="bg-card border border-border rounded-sm w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-border flex items-start justify-between">
          <div>
            <div className="meta-label">Real Mailjet send</div>
            <h3 className="font-display text-xl font-bold mt-0.5">Send test email</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Fires a single tracked email to verify the integration. The
              open + any link click will show up in this campaign&apos;s tiles
              within ~1 minute.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-sm" data-testid="email-send-close"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="meta-label">Recipient email</label>
            <input
              type="email"
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              placeholder="you@example.com"
              className="form-input w-full mt-1"
              data-testid="email-send-to"
            />
          </div>
          <div>
            <label className="meta-label">Subject (optional)</label>
            <input
              value={sendSubject}
              onChange={(e) => setSendSubject(e.target.value)}
              placeholder={`[TEST] ${stats?.campaign?.name || ""} — Mailjet integration check`}
              className="form-input w-full mt-1"
              data-testid="email-send-subject"
            />
          </div>
          <div className="text-[11px] text-muted-foreground bg-secondary/30 border border-border rounded-sm p-2">
            From: <code className="font-mono">{health?.sender_email}</code> ·
            Tracked: opens + clicks. The body is a clean HTML template with one example tracked link.
          </div>
        </div>
        <div className="p-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="btn-secondary" data-testid="email-send-cancel">Cancel</button>
          <button
            onClick={onSend}
            disabled={sending || !sendTo}
            data-testid="email-send-confirm"
            className="btn-primary inline-flex items-center gap-1 disabled:opacity-50"
          >
            {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {sending ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
